/** Mirrors the PRD §6 model. Every mutation carries source + provenance. */
export type Source = "user" | "meeting" | "scheduler" | "agent" | "calendar" | "extension";
export type TaskStatus = "open" | "scheduled" | "done" | "suggested";

export interface Task {
  id: string;
  title: string;
  notes?: string;
  status: TaskStatus;
  source: Source;
  priority: 0 | 1 | 2 | 3;
  durationMin: number;
  dueAt: Date | null;
  scheduledStart: Date | null;
  scheduledEnd: Date | null;
  eventId?: string | null;
  meetingId?: string | null;
  /** The workstream this task belongs to. Unset means "whatever its meeting was about". */
  project?: string | null;
  evidence?: { segmentSeq: number; quote: string } | null;
  confidence?: number | null;
  createdAt: Date;
  completedAt: Date | null;
}

export type EventKind = "meeting" | "focus" | "event";

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  kind: EventKind;
  flexible: boolean;
  location?: string;
  meetLink?: string;
  attendees?: number;
  source: Source;
  googleId?: string | null;
  taskId?: string | null;
  notes?: string;
}

export type MeetingStatus = "recording" | "paused" | "processing" | "complete" | "failed";

export interface Meeting {
  id: string;
  title: string;
  status: MeetingStatus;
  captureSource?: "mic" | "call";
  startedAt: Date;
  endedAt: Date | null;
  durationSec: number;
  chunkCount: number;
  transcribedCount: number;
  summarySoFar?: string;
  summary?: string;
  keyPoints?: string[];
  decisions?: { text: string; at?: string }[];
  actionItems?: { title: string; owner?: string; due?: string; confidence: number; at?: string; evidence?: string }[];
  entities?: { type: string; name: string }[];
  urls?: string[];
  openQuestions?: string[];
  researchTopics?: string[];
  /** Named workstreams this meeting touched. One meeting often covers several. */
  projects?: string[];
  manualNotes?: { at: string; text: string }[];
  notesConfirmed?: boolean;
  shareId?: string | null;
  costUsd?: number;
  silentChunks?: number;
  summaryMode?: string;
  detailedNotes?: string;
  audioRetention: "delete-after-processing" | "24h" | "7d" | "keep";
}

export interface TranscriptSegment {
  seq: number;
  text: string;
  offsetSec: number;
  durationSec: number;
}

export interface Preferences {
  workDays: number[]; // 0 = Sun
  workStart: string; // "09:00"
  workEnd: string; // "17:30"
  focusWindow: "morning" | "afternoon" | "none";
  autoNotes: boolean;
  autoTasks: boolean;
  timezone: string;
  timezoneLocked?: boolean; // set true if the user picks a timezone manually
  notifications?: { summary?: boolean; reminder?: boolean; booking?: boolean; agent?: boolean; digest?: boolean; nudge?: boolean; emailNudges?: boolean };
  onboarded: boolean;
  calendarConnected: boolean;
}

export const defaultPreferences = (): Preferences => ({
  workDays: [1, 2, 3, 4, 5],
  workStart: "09:00",
  workEnd: "17:30",
  focusWindow: "morning",
  autoNotes: true,
  autoTasks: true,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  onboarded: false,
  calendarConnected: false,
});

export type NoteType = "meeting" | "personal";
export interface Note {
  id: string;
  title: string;
  body: string;
  type: NoteType;
  meetingId?: string | null;
  linkedTasks?: number;
  updatedAt: Date;
  createdAt: Date;
}

export type ResearchStatus = "open" | "in-progress" | "ready";
export interface ResearchItem {
  id: string;
  topic: string;
  detail?: string;
  status: ResearchStatus;
  source: Source;
  meetingId?: string | null;
  url?: string | null;
  createdAt: Date;
}

export interface FocusSession {
  id: string;
  taskId: string | null;
  title: string;
  seconds: number;
  startedAt: Date;
  endedAt: Date;
}

export interface BookingPage {
  slug: string;
  title: string;
  description: string;
  durations: number[];
  bufferMin: number;
  minNoticeMin: number;
  horizonDays: number;
  dailyCap: number;
  active: boolean;
}
